import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { ClientPortfolioEntryFeeCalcInput, EntryFeeCalculationIn, EntryFeeCalculationWithAllRatiosOut } from 'src/app/services/rest-api/rest-api';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';

export interface EntryFeeCalcInput {
  changes: ClientPortfolioEntryFeeCalcInput,
  workplaceConfig?: {
      feeConfigId: number,
  }
}


/**
 * This service is intended to be used for each calling component separetely (not as a singleton).
 * Provide the service in the component's providers array.
 */
@Injectable()
export class EntryFeeCalcService {

  private entryFeeCalcSubject = new Subject<EntryFeeCalculationWithAllRatiosOut>();
  public $entryFeeCalc = this.entryFeeCalcSubject.asObservable();

  public requestStatus = {
    preloader: true,
    error: false,
  }

  public constructor(
    private restApiService: RestApiService,
    private toastService: ToastNotificationService,
    private loggerService: LoggerService,
  ) {}


  /**
   * | FrameworkContract ID | FeeConfig ID | Description |
   * |----------------------|--------------|-------------|
   * | NOT NULL             | NULL         | Request from BWM, fee based on FrameworkContractSettings |
   * | NULL                 | NOT NULL     | Request from Workplace, client not exist yet, fee based on FeeConfig |
   * | NOT NULL             | NOT NULL     | Request from Workplace, client exists already, fee based on FeeConfig |
   * | NULL                 | NULL         | Invalid |
   */
  public calculateEntryFeeToDisplay({changes, workplaceConfig}: EntryFeeCalcInput, portfolioId: number | null = null): void {
    if(this.containsEmptyValue(changes)) {
      return;
    }
    const entryFeeInput: EntryFeeCalculationIn = {
      feeConfigId: workplaceConfig?.feeConfigId,
      frameworkContractId: changes.dip.frameworkContractId,
      initialDeposit: changes.oneTimeDeposit,
      regularDeposit: changes.regularDeposit,
      instruments: Object.fromEntries(changes.instrumentsWeights),
      weeksBetweenInvestments: +changes.depositRegularity.value,
      riskScore: +changes.riskProfile,
      totalYearsToInvest: changes.timeOfInvestment,
    }

    this.requestStatus.preloader = true;

    const request = !portfolioId ? this.restApiService.calculateEntryFeeForNewInvestment(entryFeeInput)
      : this.restApiService.calculateEntryFeeForPortfolio(entryFeeInput, portfolioId)

    request.subscribe({
      next: res => this.handleCalculateEntryFeeData(res),
      error: err => this.handleCalculateEntryFeeError(err),
    });
  }

  private containsEmptyValue(changes: ClientPortfolioEntryFeeCalcInput): boolean {
    return changes.oneTimeDeposit === null ||
      isNaN(changes.oneTimeDeposit) ||
      changes.regularDeposit === null ||
      isNaN(changes.regularDeposit);
  }

  private handleCalculateEntryFeeError(error: unknown): void {
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
    this.requestStatus.error = true;
    this.requestStatus.preloader = false;
  }

  private handleCalculateEntryFeeData(data: EntryFeeCalculationWithAllRatiosOut): void {
    this.entryFeeCalcSubject.next(data);
    this.requestStatus.error = false;
    this.requestStatus.preloader = false;
  }
}
